import React from 'react';
import { Card, CardContent, Typography, Box, Grid } from '@mui/material';
import { TrendingUp } from '@mui/icons-material';
import { brand, status, text, bg, border } from '../../theme/colors';

/**
 * Payment Analytics
 * Visual charts showing payment trends and insights
 */
// Compact money formatter: keeps small amounts (e.g. a $60 Property Check)
// visible instead of rounding everything to "$0K".
const formatMoney = (value) => {
  const v = Number(value) || 0;
  if (Math.abs(v) >= 1000) {
    const k = v / 1000;
    return `$${(Number.isInteger(k) ? k : k.toFixed(1))}K`;
  }
  return `$${Math.round(v)}`;
};

const PaymentAnalytics = ({ analyticsData }) => {
  // Get spending data from analytics or use fallback
  const getSpendingData = () => {
    if (analyticsData?.spending?.byMonth) {
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const currentMonth = new Date().getMonth();
      
      // Get last 6 months
      const labels = [];
      const values = [];
      
      for (let i = 5; i >= 0; i--) {
        const monthIndex = (currentMonth - i + 12) % 12;
        const monthName = monthNames[monthIndex];
        labels.push(monthName);
        values.push(analyticsData.spending.byMonth[monthName] || 0);
      }
      
      return { labels, values };
    }
    
    // Fallback data
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
    const data = months.map(() => Math.floor(Math.random() * 50000) + 10000);
    return { labels: months, values: data };
  };

  // Get category data from analytics or use fallback
  const getCategoryData = () => {
    if (analyticsData?.categories && analyticsData.categories.length > 0) {
      // Map categories to colors
      const colorMap = {
        'Labor': brand.primary,
        'Materials': status.success,
        'Equipment': status.warning,
        'Other': '#16a085'
      };
      
      return analyticsData.categories.map(cat => ({
        label: cat.label,
        value: cat.value,
        color: colorMap[cat.label] || '#a8edea',
        amount: cat.amount
      }));
    }
    
    // Fallback data
    return [
      { label: 'Labor', value: 45, color: brand.primary, amount: 0 },
      { label: 'Materials', value: 30, color: status.success, amount: 0 },
      { label: 'Equipment', value: 15, color: status.warning, amount: 0 },
      { label: 'Other', value: 10, color: '#16a085', amount: 0 }
    ];
  };

  const spendingData = getSpendingData();
  const categoryData = getCategoryData();
  const maxSpending = Math.max(...spendingData.values, 1); // Prevent division by zero

  // Calculate summary stats from real data
  const totalSpent = analyticsData?.spending?.total || 
    spendingData.values.reduce((sum, val) => sum + val, 0);
  
  const avgMonthly = analyticsData?.spending?.average || 
    (spendingData.values.length > 0 ? totalSpent / spendingData.values.length : 0);
  
  // Calculate growth (compare last month vs average)
  const lastMonthSpending = spendingData.values[spendingData.values.length - 1] || 0;
  const growth = avgMonthly > 0 
    ? ((lastMonthSpending - avgMonthly) / avgMonthly * 100).toFixed(1)
    : 0;

  return (
    <Card sx={{ borderRadius: 2, mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <TrendingUp sx={{ color: brand.primary }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Payment Analytics
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* Spending Over Time */}
          <Grid item xs={12} md={8}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
              Spending Over Time
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 200 }}>
              {spendingData.labels.map((label, index) => {
                const value = spendingData.values[index];
                const height = maxSpending > 0 ? (value / maxSpending) * 100 : 0;
                return (
                  <Box
                    key={label}
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 600, color: brand.primary }}
                    >
                      {formatMoney(spendingData.values[index])}
                    </Typography>
                    <Box
                      sx={{
                        width: '100%',
                        height: `${Math.max(height, 5)}%`, // Minimum 5% height for visibility
                        background: brand.primary,
                        borderRadius: '8px 8px 0 0',
                        transition: 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          opacity: 0.8
                        }
                      }}
                    />
                    <Typography variant="caption" sx={{ color: text.secondary }}>
                      {label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Grid>

          {/* Payment by Category */}
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
              Payment by Category
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {categoryData.map((category) => (
                <Box key={category.label}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {category.label}
                    </Typography>
                    <Typography variant="body2" sx={{ color: text.secondary }}>
                      {category.value}%
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: '100%',
                      height: 8,
                      bgcolor: bg.subtle,
                      borderRadius: 1,
                      overflow: 'hidden'
                    }}
                  >
                    <Box
                      sx={{
                        width: `${category.value}%`,
                        height: '100%',
                        bgcolor: category.color,
                        transition: 'width 0.5s ease'
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>

        {/* Summary Stats */}
        <Box
          sx={{
            mt: 3,
            pt: 3,
            borderTop: `1px solid ${border.light}`,
            display: 'flex',
            justifyContent: 'space-around',
            flexWrap: 'wrap',
            gap: 2
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: brand.primary }}>
              {formatMoney(totalSpent)}
            </Typography>
            <Typography variant="caption" sx={{ color: text.secondary }}>
              Total Spent ({spendingData.labels.length} months)
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: status.success }}>
              {formatMoney(avgMonthly)}
            </Typography>
            <Typography variant="caption" sx={{ color: text.secondary }}>
              Avg Monthly Spending
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: growth >= 0 ? status.success : status.errorDark
              }}
            >
              {growth >= 0 ? '+' : ''}{growth}%
            </Typography>
            <Typography variant="caption" sx={{ color: text.secondary }}>
              Growth vs Avg
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default PaymentAnalytics;
